import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import AgentConfigSection from "../AgentConfigSection";
import { listDocs, listSkills } from "../../../../utils/docsApi";

export default function SkillsTab({
  enabledSkills,
  setEnabledSkills,
  docScopeMode,
  setDocScopeMode,
  configuredDocs,
  setConfiguredDocs,
}) {
  const [availableSkills, setAvailableSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [allDocs, setAllDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const selectedSkillNames = useMemo(
    () => new Set((enabledSkills || []).map((s) => String(s).toLowerCase())),
    [enabledSkills]
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingSkills(true);
    listSkills()
      .then((res) => {
        if (cancelled) return;
        setAvailableSkills(
          res.ok && Array.isArray(res.data) ? res.data : []
        );
      })
      .finally(() => !cancelled && setLoadingSkills(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (docScopeMode !== "SELECTED" || allDocs.length > 0) return;
    let cancelled = false;
    setLoadingDocs(true);
    listDocs()
      .then((res) => {
        if (cancelled) return;
        setAllDocs(res.ok && Array.isArray(res.data) ? res.data : []);
      })
      .finally(() => !cancelled && setLoadingDocs(false));
    return () => {
      cancelled = true;
    };
  }, [docScopeMode, allDocs.length]);

  const toggleSkill = (skillName) => {
    const name = String(skillName).toLowerCase();
    if (selectedSkillNames.has(name)) {
      setEnabledSkills(
        (enabledSkills || []).filter(
          (s) => String(s).toLowerCase() !== name
        )
      );
    } else {
      setEnabledSkills([...(enabledSkills || []), name]);
    }
  };

  const selectedDocOptions = useMemo(() => {
    const byId = new Map(allDocs.map((d) => [d.docId, d]));
    return (configuredDocs || []).map((doc) => {
      const docId = typeof doc === "string" ? doc : doc.docId;
      const known = byId.get(docId);
      return {
        docId,
        title:
          (typeof doc === "object" && doc.title) ||
          known?.title ||
          docId,
      };
    });
  }, [configuredDocs, allDocs]);

  return (
    <>
      <AgentConfigSection id="enabledSkills">
        <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 1.5 }}>
          Skills are reusable playbooks/SOPs stored in Docs. The skills you
          enable here are listed in this agent's system prompt as a catalog; the
          agent calls <code>load_skill</code> to read a skill's full
          instructions when a task matches.
        </Typography>

        {loadingSkills ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : availableSkills.length === 0 ? (
          <Box
            sx={{
              p: 2,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: "text.secondary",
            }}
          >
            <AutoStoriesOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "13px" }}>
              No skills exist yet. Create a doc and promote it to a skill from
              the Docs page, then enable it here.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {availableSkills.map((skill) => {
              const name = skill.skillName || "";
              return (
                <FormControlLabel
                  key={skill.docId || name}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedSkillNames.has(name.toLowerCase())}
                      onChange={() => toggleSkill(name)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                        {name || skill.title}
                      </Typography>
                      {skill.title && skill.title !== name && (
                        <Typography
                          sx={{ fontSize: "12px", color: "text.secondary" }}
                        >
                          {skill.title}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", m: 0 }}
                />
              );
            })}
          </Box>
        )}
      </AgentConfigSection>

      <AgentConfigSection id="docsAccess">
        <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 1.5 }}>
          Controls which docs this agent can read and edit through the doc tools
          (read_doc, search_docs, update_doc, etc.).
        </Typography>
        <RadioGroup
          value={docScopeMode === "SELECTED" ? "SELECTED" : "ALL"}
          onChange={(e) => setDocScopeMode(e.target.value)}
        >
          <FormControlLabel
            value="ALL"
            control={<Radio size="small" />}
            label={
              <Typography sx={{ fontSize: "13px" }}>
                All docs in this organization
              </Typography>
            }
          />
          <FormControlLabel
            value="SELECTED"
            control={<Radio size="small" />}
            label={
              <Typography sx={{ fontSize: "13px" }}>
                Only selected docs (and their sub-pages)
              </Typography>
            }
          />
        </RadioGroup>

        {docScopeMode === "SELECTED" && (
          <Box sx={{ mt: 1.5 }}>
            <Typography className="org-field-label">
              Allowed doc subtrees
            </Typography>
            <Autocomplete
              multiple
              size="small"
              loading={loadingDocs}
              options={allDocs}
              getOptionLabel={(option) => option.title || option.docId}
              isOptionEqualToValue={(option, value) =>
                option.docId === value.docId
              }
              value={selectedDocOptions}
              onChange={(_e, value) =>
                setConfiguredDocs(
                  value.map((v) => ({ docId: v.docId, title: v.title || "" }))
                )
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.title || option.docId}
                    {...getTagProps({ index })}
                    key={option.docId}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="org-styled-textfield"
                  placeholder="Add a doc..."
                />
              )}
            />
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
            >
              The agent may access these docs and everything nested under them.
              With no docs selected, the agent has no doc access.
            </Typography>
          </Box>
        )}
      </AgentConfigSection>
    </>
  );
}
